import React, { FunctionComponent, useContext, useState, useEffect } from 'react';
import { IZipAvailability, IStoreAvailability, IStore } from '@inter-ikea-kompis/types/lib';
import { getStoreId } from 'utils/queryUtils';
import { useServiceSettings } from 'contexts/serviceSettings';
import { useSelector } from 'react-redux';
import {
  ApplicationAvailabilityService,
  ZipCodeCookieInformationService,
  StoreIdCookieInformationService,
  StoreService,
  ZipValidationService,
  CheckoutAvailabilityService
} from '@inter-ikea-kompis/services';
import { Product } from '@inter-ikea-storageone/range';
import { StoreAvailabilityProbabilityEnum } from '@inter-ikea-kompis/enums/lib';
import { getShoppingProducts } from 'ducks/products';
import { isKiosk } from 'utils/platform';
import { reportCurrentLocation } from 'analytics/reporters';

type AvailabilityProviderProps = {
  zipCode: string | null;
  storeId: string | null;
  stores: IStore[];
  zipAvailabilities: IZipAvailability[];
  storeAvailabilities: IStoreAvailability[];
  getSelectedStoreName: () => string;
  validateZipCode: (zipCode: string) => Promise<string | undefined | null>;
  getProductHomeDelivery: (product: Product) => boolean | undefined;
  getProductStoreAvailability: (product: Product) => StoreAvailabilityProbabilityEnum | undefined;
  getHomeDeliveryForProducts: (productIds: string[]) => IZipAvailability[] | undefined;
  getStoreAvailabilityForProducts: (productIds: string[]) => IStoreAvailability[] | undefined;
  updateHomeDeliveryForShoppingProducts: (zipCode: string) => void;
  updateStoreAvailabilityForShoppingProducts: () => void;
};

export const AvailabilityContext = React.createContext<AvailabilityProviderProps>({
  zipCode: null,
  storeId: null,
  stores: [],
  zipAvailabilities: [],
  storeAvailabilities: [],
  getSelectedStoreName: () => '',
  validateZipCode: Promise.resolve,
  getProductHomeDelivery: () => undefined,
  getProductStoreAvailability: () => undefined,
  getHomeDeliveryForProducts: () => undefined,
  getStoreAvailabilityForProducts: () => undefined,
  updateHomeDeliveryForShoppingProducts: () => {},
  updateStoreAvailabilityForShoppingProducts: () => {}
});

export const useAvailability = () => useContext(AvailabilityContext);

type Props = {
  children: React.ReactNode;
};

export const AvailabilityProvider: FunctionComponent<Props> = ({ children }) => {
  const [zipCode, updateZipCode] = useState<string | null>(null);
  const [storeId, updateStoreId] = useState<string | null>(null);
  const [stores, setStores] = useState<IStore[]>([]);
  const [zipAvailabilities, setZipAvailabilities] = useState<IZipAvailability[]>([]);
  const [storeAvailabilities, setStoreAvailabilities] = useState<IStoreAvailability[]>([]);
  const [zipAvailabilitiesForShopping, setZipAvailabilitiesForShopping] = useState<
    IZipAvailability[]
  >([]);
  const [storeAvailabilitiesForShopping, setStoreAvailabilitiesForShopping] = useState<
    IStoreAvailability[]
  >([]);

  const { kompisSettings, dexfSettings } = useServiceSettings();
  const zipService = new ZipCodeCookieInformationService({ settings: dexfSettings });
  const storeService = new StoreIdCookieInformationService({ settings: dexfSettings });

  const shoppingItems = useSelector(getShoppingProducts()).map(shoppingProduct => ({
    type: shoppingProduct.shoppingItem.type,
    id: shoppingProduct.shoppingItem.id
  }));

  const appId = kompisSettings.applicationName;
  const serviceOptions = {
    locale: kompisSettings.locale,
    settings: dexfSettings,
    dexfApiKey: kompisSettings.dexfApiKey
  };
  const applicationService = new ApplicationAvailabilityService(serviceOptions);
  const checkoutAvailabilityService = new CheckoutAvailabilityService(serviceOptions);

  const updateHomeDeliveryForShoppingProducts = async (zipCode: string) => {
    try {
      const result = await checkoutAvailabilityService.getZipAvailabilities(zipCode, shoppingItems);
      setZipAvailabilitiesForShopping(result);
    } catch (e) {
      // Unsure how kompis want's us to handle the error from the validation, a bit unclear in the documentation as of this time.
      // Will just wrap this for now so we don't get errors in sentry
    }
  };

  const updateStoreAvailabilityForShoppingProducts = async () => {
    if (storeId) {
      const result = await checkoutAvailabilityService.getStoreAvailabilities(
        storeId,
        shoppingItems
      );
      setStoreAvailabilitiesForShopping(result);
    }
  };

  const setZipCode = async (newZipCode: string | null) => {
    if (!isKiosk()) {
      reportCurrentLocation({ post_code: newZipCode, store_id: storeId });
    }
    if (newZipCode) {
      await updateHomeDeliveryForShoppingProducts(newZipCode);

      try {
        const result = await applicationService.getZipAvailabilities(newZipCode, appId);
        setZipAvailabilities(result);
      } catch (e) {
        // Unsure how kompis want's us to handle the error from the validation, a bit unclear in the documentation as of this time.
        // Will just wrap this for now so we don't get errors in sentry
      }
    } else {
      setZipAvailabilities([]);
      setZipAvailabilitiesForShopping([]);
    }
    updateZipCode(newZipCode);
  };

  const setStoreId = async (newStoreId: string | null) => {
    if (!isKiosk()) {
      reportCurrentLocation({ post_code: zipCode, store_id: newStoreId });
    }

    if (newStoreId) {
      await updateStoreAvailabilityForShoppingProducts();

      const result = await applicationService.getStoreAvailabilities(newStoreId, appId);
      setStoreAvailabilities(result);
    } else {
      setStoreAvailabilities([]);
      setStoreAvailabilitiesForShopping([]);
    }
    updateStoreId(newStoreId);
  };

  const getSelectedStoreName = () => {
    const store = stores.find(store => store.id === storeId);
    return store?.name || '';
  };

  const validateZipCode = async (code: string) => {
    if (dexfSettings.kompis.serviceSettings.zipValidationDataSource === 'disabled') {
      return null;
    }
    const validationService = new ZipValidationService(serviceOptions);
    let result;

    try {
      result = await validationService.getZipValidation(code);
    } catch {
      return null;
    }
    // Always return the formatted zip if the result is valid
    return result.valid ? result.content?.formatted : null;
  };

  const getProductHomeDelivery = (product: Product) => {
    const shoppingAvailability = zipAvailabilitiesForShopping.find(
      availability => availability.itemId === product.globalItemNo
    );

    if (shoppingAvailability) return shoppingAvailability.content.availableForHomeDelivery;

    const availability =
      product &&
      zipAvailabilities.find(availability => availability.itemId === product.globalItemNo);
    return availability?.content.availableForHomeDelivery;
  };

  const getProductStoreAvailability = (product: Product) => {
    const shoppingAvailability = storeAvailabilitiesForShopping.find(
      availability => availability.itemId === product.globalItemNo
    );

    if (shoppingAvailability) return shoppingAvailability.content.cashCarry?.messageType;

    const availability =
      product && storeAvailabilities.find(p => p.itemId === product.globalItemNo);
    return availability?.content.cashCarry?.messageType;
  };

  const getHomeDeliveryForProducts = (productsId: string[]) => {
    if (zipCode && zipAvailabilities.length > 0) {
      const shoppingAvailability = zipAvailabilitiesForShopping.filter(availability =>
        productsId.includes(availability.itemId)
      );
      const applicationAvailability = zipAvailabilities.filter(
        availability =>
          !shoppingAvailability.map(a => a.itemId).includes(availability.itemId) &&
          productsId.includes(availability.itemId)
      );

      return [...shoppingAvailability, ...applicationAvailability];
    }
    return undefined;
  };

  const getStoreAvailabilityForProducts = (productsId: string[]) => {
    if (storeId && storeAvailabilities.length > 0) {
      const shoppingAvailability = storeAvailabilitiesForShopping.filter(availability =>
        productsId.includes(availability.itemId)
      );
      const applicationAvailability = storeAvailabilities.filter(
        availability =>
          !shoppingAvailability.map(a => a.itemId).includes(availability.itemId) &&
          productsId.includes(availability.itemId)
      );

      return [...shoppingAvailability, ...applicationAvailability];
    }
    return undefined;
  };

  useEffect(() => {
    if (dexfSettings.kompis.serviceSettings.zipValidationDataSource !== 'disabled') {
      zipService.subscribe(zipCode => {
        setZipCode(zipCode);
      });
    }

    if (dexfSettings.kompis.serviceSettings.storeAvailabilityDataSource !== 'disabled') {
      storeService.subscribe(storeId => {
        setStoreId(storeId);
      });
    }

    const storeIdFromQuery = getStoreId();
    if (storeIdFromQuery) {
      storeService.setStoreId(storeIdFromQuery);
    } else {
      storeService
        .getStoreId()
        .then(storeIdFromService => {
          if (storeIdFromService) {
            setStoreId(storeIdFromService);
          }
        })
        .catch(e => {
          // eslint-disable-next-line no-console
          console.warn(e);
        });
    }

    // Set zipcode on fist use of availability context. Needed for example when starting
    // the planner with a storeId set. Will only run once.
    zipService
      .getZipCode()
      .then((zipCode: any) => {
        if (zipCode) {
          setZipCode(zipCode);
        }
      })
      .catch(e => {
        // eslint-disable-next-line no-console
        console.warn(e);
      });

    return () => {
      storeService.unsubscribe(storeId => setStoreId(storeId));
      zipService.unsubscribe(zipCode => setZipCode(zipCode));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const getStores = async () => {
      const service = new StoreService(kompisSettings);
      const stores = await service.getStores();
      setStores(stores);
      return stores;
    };

    getStores();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeId]);

  return (
    <AvailabilityContext.Provider
      value={{
        zipCode,
        storeId,
        stores,
        zipAvailabilities,
        storeAvailabilities,
        getSelectedStoreName,
        validateZipCode,
        getProductHomeDelivery,
        getProductStoreAvailability,
        getHomeDeliveryForProducts,
        getStoreAvailabilityForProducts,
        updateHomeDeliveryForShoppingProducts,
        updateStoreAvailabilityForShoppingProducts
      }}
    >
      {children}
    </AvailabilityContext.Provider>
  );
};
